import { ArrowUpFromLine, CheckCheck, Reply, Send, Star, Trash2, X, File } from 'lucide-react';
import React, { Fragment, memo, useEffect, useMemo, useRef, useState, useCallback } from 'react'
import VideoPlayer from '../components/VideoPlayer';
import AudioPlayer from '../components/AudioPlayer';
import LinkHighlighter from '../components/LinkHighlighter';
import BeautifiedCodeViewer from '../components/BeautifiedCodeViewer';
import MapCard from '../components/MapCard';

function GroupChat(props) {

    const {
        chatMessages,
        messagesContainerRef,
        highlightReply,
        user,
        isReceiver,
        senderContainerColor,
        receiverContainerColor,
        senderTextColor,
        receiverTextColor,
        authUserId,
        checkFileType,
        formatFile,
        openInNewTab,
        isProbablyCode,
        handleReplyMessage,
        handleDeleteGroupMessage,
        senderDateTextColor,
        receiverDateTextColor,
        headerTextColor,
        getColorByName,
        scrollToMessage,
        color,
        senderLinkTextColor,
        receiverLinkTextColor
    } = props;

    const divRef = useRef();
    const [page, setPage] = useState(1);
    const pageSize = 10;
    const [prevScrollHeight, setPrevScrollHeight] = useState(null);
    const [isScrolling, setIsScrolling] = useState(false);

    // Memoize flattened and sorted chats
    const allChats = useMemo(() => {
        if (!Array.isArray(chatMessages)) return [];
        return chatMessages
            .flatMap(day => Array.isArray(day?.chats) ? day : day)
            .sort((a, b) => new Date(b.date) - new Date(a.date));
    }, [chatMessages]);

    // Memoize visible chats
    const visibleChats = useMemo(() => {
        return allChats.slice(0, page * pageSize);
    }, [allChats, page]);

    // Optimized scroll handler with debouncing
    const handleScroll = useCallback(() => {
        if (isScrolling) return;

        const el = divRef.current;
        if (!el) return;

        setIsScrolling(true);

        // Improved scroll detection for reverse flex direction
        const scrollTop = Math.abs(el.scrollTop);
        const scrollHeight = el.scrollHeight;
        const clientHeight = el.clientHeight;
        const threshold = 100;

        if (scrollTop + clientHeight + threshold >= scrollHeight) {
            const prevScrollHeight = el.scrollHeight;
            setPrevScrollHeight(prevScrollHeight);
            setPage(prevPage => prevPage + 1);
        }

        // Debounce scroll events
        setTimeout(() => setIsScrolling(false), 100);
    }, [isScrolling]);

    // Effect to maintain scroll position after loading new messages
    useEffect(() => {
        if (prevScrollHeight === null) return;

        const el = divRef.current;
        if (!el) return;

        requestAnimationFrame(() => {
            const newScrollHeight = el.scrollHeight;
            const diff = newScrollHeight - prevScrollHeight;
            if (diff > 0) {
                el.scrollTop -= diff; // Adjust for reverse flex direction
            }
            setPrevScrollHeight(null);
        });
    }, [visibleChats.length, prevScrollHeight]);

    // Render user avatar component
    const renderUserAvatar = useCallback((user) => {
        if (!user) return null;

        return user.profileImage ? (
            <img
                src={user.profileImage}
                alt={user.name?.charAt(0) || 'U'}
                className="w-6 h-6 rounded-full mr-1 self-end mb-5 border"
                loading="lazy"
            />
        ) : (
            <span className="w-6 h-6 rounded-full mr-1 flex items-center mb-5 justify-center self-end border text-xs text-black bg-gray-200">
                {user.name?.charAt(0) || 'U'}
            </span>
        );
    }, []);

    // Render file preview component
    const renderFilePreview = useCallback((message) => {
        const fileType = checkFileType(message.fileType);

        switch (fileType) {
            case 'image':
                return (
                    <div className="flex justify-center items-center mb-1">
                        <img
                            onClick={() => openInNewTab(message.link)}
                            src={message.link}
                            alt="Preview"
                            className="max-h-28 rounded cursor-pointer"
                            loading="lazy"
                        />
                    </div>
                );
            case 'video':
                return <VideoPlayer src={message.link} color={color} />;
            case 'audio':
                return <AudioPlayer src={message.link} color={color} />;
            case 'application':
                return (
                    <div
                        className="flex items-center bg-gray-100 p-2 rounded cursor-pointer mb-1"
                        onClick={() => openInNewTab(message.link)}
                    >
                        <File size={20} className="text-black mr-2" />
                        <p className="text-sm text-black">Document{formatFile(message.link)}</p>
                    </div>
                );
            default:
                return null;
        }
    }, [checkFileType, openInNewTab, formatFile, color]);

    // Render reply preview
    const renderReplyPreview = useCallback((message) => {
        if (!message?.replyMessageId) return null;

        return (
            <div
                className={`p-2 rounded border-l-4 cursor-pointer ${message.isSent
                    ? 'bg-gray-600 border-teal-600'
                    : 'bg-gray-100 border-teal-600'
                    }`}
                onClick={(e) => {
                    e.stopPropagation();
                    scrollToMessage(message.replyMessageId._id);
                }}
            >
                <div className="text-xs font-medium mb-1 text-black">
                    {message?.replyMessageId?.userId?._id === authUserId
                        ? "You"
                        : message?.replyMessageId?.userId?.name || "Unknown User"
                    }
                </div>
                <div className="text-xs text-gray-700 truncate">
                    {message?.replyMessageId?.messageContent || "Message content unavailable"}
                </div>
            </div>
        );
    }, [authUserId, scrollToMessage]);

    // Render message actions
    const renderMessageActions = useCallback((message) => {
        if (message.isDeleted) return null;

        return (
            <div
                className={`absolute top-1/2 -translate-y-1/2 z-10 flex items-center gap-2 px-2
                    ${message.sender === 'me' ? 'right-full ml-2' : 'left-full mr-2'}
                    opacity-0 group-hover:opacity-100 transition-opacity`}
            >
                <Reply
                    onClick={() => handleReplyMessage(message)}
                    className="text-gray-400 hover:text-primary cursor-pointer"
                    size={18}
                />
                {message.sender === 'me' && (
                    <Trash2
                        onClick={() => handleDeleteGroupMessage(message)}
                        className="text-gray-400 hover:text-primary cursor-pointer"
                        size={18}
                    />
                )}
            </div>
        );
    }, [handleReplyMessage, handleDeleteGroupMessage]);

    // Render message content
    const renderMessageContent = useCallback((message) => {
        const isMe = message.sender === 'me';

        if (message.isDeleted) {
            return (
                <div
                    className={`p-2 text-xs italic rounded-xl shadow-md whitespace-pre-wrap break-words  
                        ${isMe ? 'rounded-br-none' : 'rounded-bl-none'}`}
                    style={{
                        backgroundColor: isMe ? senderContainerColor : receiverContainerColor,
                        color: isMe ? senderTextColor : receiverTextColor,
                    }}
                >
                    {isMe ? 'You deleted this message' : "This message was deleted"}
                </div>
            );
        }

        return (
            <div
                className={`p-2 rounded-lg shadow-md break-words whitespace-pre-wrap ${isMe
                    ? 'bg-blue-500 text-white rounded-br-none'
                    : 'bg-gray-200 text-gray-800 rounded-bl-none'
                    }`}
                style={{
                    backgroundColor: isMe ? senderContainerColor : receiverContainerColor,
                    color: isMe ? senderTextColor : receiverTextColor
                }}
            >
                {renderReplyPreview(message)}
                {renderFilePreview(message)}

                {message.type === "L" && (
                    <MapCard data={message.text} />
                )}

                <div className="text-sm">
                    {!isProbablyCode(message.text) &&
                        checkFileType(message.fileType) !== "image" &&
                        message.text !== '' &&
                        message.type !== 'L' && (
                            <LinkHighlighter
                                text={message.text}
                                color={isMe ? senderLinkTextColor : receiverLinkTextColor}
                            />
                        )}
                    {isProbablyCode(message.text) && (
                        <BeautifiedCodeViewer code={message?.text} />
                    )}
                </div>
            </div>
        );
    }, [senderContainerColor, receiverContainerColor, senderTextColor, receiverTextColor,
        renderReplyPreview, renderFilePreview, isProbablyCode, checkFileType,
        senderLinkTextColor, receiverLinkTextColor]);

    return (
        <Fragment>
            <div
                ref={divRef}
                onScroll={handleScroll}
                className="flex-1 p-3 overflow-y-auto flex flex-col-reverse"
            >
                {visibleChats.map((chat, chatIndex) => (
                    <div
                        ref={messagesContainerRef}
                        key={`chat-${chatIndex}-${chat?.date || chatIndex}`}
                    >
                        <div className="text-xs bg-primary/20 text-primary px-2 py-1 mx-auto my-4 backdrop-blur-sm border-b w-full flex items-center justify-center border-gray-500">
                            {chat?.date || 'Unknown Date'}
                        </div>

                        {Array.isArray(chat?.chats) && [...chat.chats].reverse().map((message, index) => {
                            if (!message || message.type === 'E') {
                                return message?.type === 'E' ? (
                                    <div
                                        key={`event-${message._id || index}`}
                                        className="text-xs bg-primary/20 text-primary px-2 py-1 rounded-md mx-auto my-1.5 w-fit backdrop-blur-sm"
                                        style={{
                                            backgroundColor: color,
                                            color: headerTextColor,
                                        }}
                                    >
                                        {message.text || 'Event message'}
                                    </div>
                                ) : null;
                            }

                            const isMe = message.sender === 'me';
                            const isReceiver = message.sender === 'receiver'; // Fixed typo

                            return (
                                <div
                                    id={`message-${message._id}`}
                                    key={`message-${message._id || index}`}
                                    className={`mb-2 flex ${isMe ? 'justify-end' : 'justify-start'} ${highlightReply === message._id ? 'transition-colors duration-1000 bg-yellow-100 rounded-xl' : ""
                                        }`}
                                >
                                    {!isMe && renderUserAvatar(user)}

                                    <div className="flex flex-col max-w-[80%] relative group">
                                        {isReceiver && (
                                            <span
                                                className="text-xs font-semibold mb-1"
                                                style={{ color: getColorByName(message?.name) }}
                                            >
                                                {message?.name || 'Unknown User'}
                                            </span>
                                        )}

                                        {renderMessageContent(message)}
                                        {renderMessageActions(message)}

                                        <div
                                            className={`text-xs mt-1 flex gap-1 items-center ${message.sender === 'me' ? 'justify-end text-gray-600' : 'justify-start text-gray-800'
                                                }`}
                                            style={{
                                                color: message.sender === 'me' ? senderDateTextColor : receiverDateTextColor,
                                            }}
                                        >
                                            {(message.sender === 'me' && message.isStared) && (
                                                <span><Star fill='#eab308' className='text-yellow-500' size={10} /></span>
                                            )}
                                            <span>{message.time || 'Unknown time'}</span>
                                            {message.sender !== 'me' && message.isStared && (
                                                <span><Star fill='#eab308' className='text-yellow-500' size={10} /></span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        </Fragment>
    );
}

export default memo(GroupChat);